import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { debounceTime, filter, finalize, map, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from '@core/services/storage.service';
import { StoreOpeningHoursViewModel, StoreViewModel } from '@models/api.models';
import { GetStoreParams } from '@features/stores-overview/model/stores';
import { ClubService } from '@features/club/service/club.service';
import { AuthenticationService } from '@core/authentication/authentication.service';

const storeStorageKey = 'store';

@Injectable({
    providedIn: 'root'
})
export class StoresService {
    private readonly regionsRs$: ReplaySubject<string[]> = new ReplaySubject(1);
    private readonly storesRs$: ReplaySubject<StoreViewModel[]> = new ReplaySubject(1);
    private readonly allStoresRs$: ReplaySubject<StoreViewModel[]> = new ReplaySubject(1);
    private readonly searchParamsBs$ = new BehaviorSubject<GetStoreParams | null>(null);
    private readonly favoriteStoreBs$ = new BehaviorSubject<StoreViewModel | null>(null);
    private readonly pendingBs$ = new BehaviorSubject<boolean>(false);

    public regions$ = this.regionsRs$.asObservable();
    public stores$ = this.storesRs$.asObservable();
    public allStores$ = this.allStoresRs$.asObservable();
    public favoriteStore$ = this.favoriteStoreBs$.asObservable();
    public pending$ = this.pendingBs$.asObservable();

    constructor(
        private readonly http: HttpClient,
        private readonly storage: StorageService,
        private readonly clubService: ClubService,
        private readonly authenticationService: AuthenticationService
    ) {
        this.getStores().subscribe(stores => this.allStoresRs$.next(stores));

        this.allStores$
            .pipe(
                withLatestFrom(this.authenticationService.loginState$),
                filter(([, { loggedIn }]) => loggedIn),
                switchMap(([stores]) => combineLatest([of(stores), this.clubService.getUserInfo()]))
            )
            .subscribe(([stores, userInfo]) => {
                const favoriteStore = stores.find(({ storeId }) => String(storeId) === userInfo.favoriteStoreId);
                if (!favoriteStore) {
                    return;
                }
                this.setFavoriteStore(favoriteStore);
            });

        this.searchParamsBs$
            .pipe(
                skip(1),
                debounceTime(200),
                switchMap(params => (params ? this.getStores(params) : of([])))
            )
            .subscribe(stores => {
                this.storesRs$.next(stores);
            });

        const storeFromStorage = this.getStoreFromStorage();
        if (storeFromStorage) {
            this.favoriteStoreBs$.next(storeFromStorage);
        }
    }

    public resetFilter(): void {
        this.searchParamsBs$.next(null);
    }

    public searchStoresBy(params: GetStoreParams | null): void {
        this.searchParamsBs$.next(params);
    }

    public getStoreById(storeId: number): Observable<StoreViewModel | undefined> {
        return this.allStores$.pipe(map(stores => stores.find(store => store.storeId === Number(storeId))));
    }

    public filterStoresByRegion(regions: string[]): void {
        this.regionsRs$.next(regions);
    }

    public getStoreOpeningHoursById(storeId?: number): Observable<StoreOpeningHoursViewModel> {
        if (storeId) {
            return this.http.get<StoreOpeningHoursViewModel>(`stores/${storeId}/openinghours`);
        }

        return of();
    }

    public setFavoriteStore(store: StoreViewModel): void {
        this.storage.set<StoreViewModel>(storeStorageKey, store);
        this.favoriteStoreBs$.next(store);
    }

    public removeFavoriteStore(): void {
        this.storage.remove(storeStorageKey);
        this.favoriteStoreBs$.next(null);
    }

    public filterStores(search: string, stores: StoreViewModel[]): StoreViewModel[] {
        search = search.toLowerCase();
        return stores.filter(
            store =>
                store.storeName?.toLowerCase().includes(search) ||
                store.postalCode?.toLowerCase().startsWith(search) ||
                store.city?.toLowerCase().startsWith(search) ||
                store.address?.toLowerCase().startsWith(search)
        );
    }

    public getStores(params?: GetStoreParams): Observable<StoreViewModel[]> {
        this.pendingBs$.next(true);
        return this.http.get<StoreViewModel[]>('stores', { params }).pipe(
            finalize(() => this.pendingBs$.next(false)),
            tap(stores => this.getRegions(stores))
        );
    }

    private getStoreFromStorage(): StoreViewModel | undefined {
        return this.storage.get<StoreViewModel>(storeStorageKey);
    }

    private getRegions(stores: StoreViewModel[]): void {
        const regions = stores.reduce((acc: string[], store) => {
            if (store.region && acc.indexOf(store.region) === -1 && Boolean(store.latitude && store.longitude)) {
                acc.push(store.region);
            }
            return acc;
        }, []);

        this.regionsRs$.next(regions);
    }
}
